/**
 * Agent Configuration Page
 *
 * UI for managing AI agents and customizing their behavior.
 * Allows organization admins to view agents, configure settings, and test execution.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import agentManagementService from '../services/agentManagementService';
import AgentGrid from '../components/agents/AgentGrid';
import AgentDetailDialog from '../components/agents/AgentDetailDialog';
import AgentConfigDialog from '../components/agents/AgentConfigDialog';
import AgentTestDialog from '../components/agents/AgentTestDialog';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function AgentConfiguration() {
  const [tabValue, setTabValue] = useState(0);
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialogs
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  // Filter agents when search or category changes
  useEffect(() => {
    filterAgents();
  }, [searchQuery, selectedCategory, agents]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [agentsData, categoriesData] = await Promise.all([
        agentManagementService.listAgents(),
        agentManagementService.getAgentCategories()
      ]);

      setAgents(agentsData);
      setCategories(['all', ...categoriesData]);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load agent data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterAgents = () => {
    let filtered = agents;

    // Filter by category
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(agent =>
        agent.category === selectedCategory
      );
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(agent =>
        agent.name.toLowerCase().includes(query) ||
        agent.agent_id.toLowerCase().includes(query) ||
        (agent.description && agent.description.toLowerCase().includes(query))
      );
    }

    setFilteredAgents(filtered);
  };

  const handleRefresh = () => {
    loadData();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 0) {
      setSelectedCategory('all');
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setTabValue(1);
  };

  const handleAgentClick = (agent) => {
    setSelectedAgent(agent);
    setDetailDialogOpen(true);
  };

  const handleConfigureClick = (agent) => {
    setSelectedAgent(agent);
    setConfigDialogOpen(true);
  };

  const handleTestClick = (agent) => {
    setSelectedAgent(agent);
    setTestDialogOpen(true);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  if (loading && agents.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading agents...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadData}>
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PsychologyIcon fontSize="large" />
            AI Agent Configuration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and customize AI agents for your organization
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            placeholder="Search agents..."
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <IconButton onClick={handleRefresh} color="primary" disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ p: 2, bgcolor: 'primary.light', color: 'white', borderRadius: 1 }}>
            <Typography variant="h4">{agents.length}</Typography>
            <Typography variant="body2">Total Agents</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ p: 2, bgcolor: 'success.light', color: 'white', borderRadius: 1 }}>
            <Typography variant="h4">{categories.length - 1}</Typography>
            <Typography variant="body2">Categories</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ p: 2, bgcolor: 'info.light', color: 'white', borderRadius: 1 }}>
            <Typography variant="h4">{filteredAgents.length}</Typography>
            <Typography variant="body2">Filtered Results</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ p: 2, bgcolor: 'warning.light', color: 'white', borderRadius: 1 }}>
            <Typography variant="h4">0</Typography>
            <Typography variant="body2">Custom Configs</Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="All Agents" />
          <Tab label="By Category" icon={<FilterListIcon />} iconPosition="end" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <AgentGrid
          agents={filteredAgents}
          onAgentClick={handleAgentClick}
          onConfigureClick={handleConfigureClick}
          onTestClick={handleTestClick}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Select Category</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'contained' : 'outlined'}
                onClick={() => handleCategoryChange(category)}
                size="small"
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </Button>
            ))}
          </Box>
        </Box>
        <AgentGrid
          agents={filteredAgents}
          onAgentClick={handleAgentClick}
          onConfigureClick={handleConfigureClick}
          onTestClick={handleTestClick}
        />
      </TabPanel>

      {/* Dialogs */}
      <AgentDetailDialog
        open={detailDialogOpen}
        agent={selectedAgent}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedAgent(null);
        }}
      />

      <AgentConfigDialog
        open={configDialogOpen}
        agent={selectedAgent}
        onClose={() => {
          setConfigDialogOpen(false);
          setSelectedAgent(null);
        }}
        onSave={loadData}
      />

      <AgentTestDialog
        open={testDialogOpen}
        agent={selectedAgent}
        onClose={() => {
          setTestDialogOpen(false);
          setSelectedAgent(null);
        }}
      />
    </Container>
  );
}
